import { IconAlertTriangleFilled } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { mapCategoryToAliasTranslation } from '@/utils/categoryUtils';
import { formatDate } from '@/utils/date';
import { getDueDateColor, getDueDateString } from '@/utils/dueDate';
import { getProgressBarText } from '@/utils/generateProgressBarText';
import { generateProgressText } from '@/utils/generateProgressText';
import { getNameFromLanguage } from '@/utils/language';
import { getTimeSpentOnPlan } from '@/utils/time';

import PlanCard from '@/components/Cards/PlanCard/PlanCard';
import PlanCardIcon from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardIcon';
import PlanCardLabel from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardLabel';
import PlanCardSubHeader from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardSubHeader';
import PlanCardTag from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardTag';
import PlanCardTitle from '@/components/Cards/PlanCard/PlanCardComponents/PlanCardTitle';
import PlanCardContent from '@/components/Cards/PlanCard/PlanCardContent/PlanCardContent';

import { Certificate, LanguageObject } from '@/types/types';

import Constants from 'constants/index';

import { Ribbon } from '@/icons';

interface Course {
  certificate: Certificate;
  courseName: LanguageObject;
  createdDate: string;
  enabled: boolean;
  id: string;
  completedDate: string;
  dueDate: number | string;
  duration: number | string;
  enrolledDate: string;
  enrollmentId: string;
  finalScore: number | string;
  hidden: boolean;
  mostRecentCompletedDate: string;
  pathwayCourseId: string;
  percentComplete: number | string;
  startedDate: string;
  status: string;
}
interface MyPlansCompliancePlanCardProps {
  course: Course;
}

const MyPlansCompliancePlanCard: React.FC<MyPlansCompliancePlanCardProps> = ({
  course,
}) => {
  const { t } = useTranslation();
  // TODO: needs to be fixed when we clean up plan types
  const timeSpentOnPlan = getTimeSpentOnPlan(
    course as unknown as Parameters<typeof getTimeSpentOnPlan>[0],
  );
  const isComplete =
    course.status === Constants.LEARN_UPON_TRAINING_PLANS.COMPLETED ||
    course.status === Constants.LEARN_UPON_TRAINING_PLANS.PASSED;
  const isFailed = course.status === Constants.LEARN_UPON_TRAINING_PLANS.FAILED;
  const finalScore = course.finalScore;
  const isCompliance = true;
  const isFoodSafety = getNameFromLanguage(course.courseName).includes(
    Constants.FOOD_SAFETY_COURSE_NAME,
  );
  const percentComplete = course.percentComplete;

  return (
    <PlanCard
      category={Constants.PLAN_CATEGORIES.COMPLIANCE}
      dataTestId={'MyCompliancePlan'}
      link={`/${Constants.ROUTE_PATH_NAMES.COMPLIANCE_PATH_NAME}/${Constants.ROUTE_PATH_NAMES.DOCUMENT_PATH_NAME}/${course.pathwayCourseId}/true`}
      progress={isComplete || isFailed ? null : (percentComplete as number)}
    >
      <PlanCardContent>
        <PlanCardLabel>
          {t(
            mapCategoryToAliasTranslation(Constants.PLAN_CATEGORIES.COMPLIANCE),
          )}
        </PlanCardLabel>
        <PlanCardTitle>{getNameFromLanguage(course.courseName)}</PlanCardTitle>
        {isComplete && (
          <>
            <PlanCardSubHeader className="success" data-testid="PlanTaskCount">
              {generateProgressText({
                finalScore: finalScore as string,
                isCompliance,
                isComplete,
                isFailed,
                isFoodSafety,
                timeSpentOnPlan: timeSpentOnPlan,
              })}
            </PlanCardSubHeader>
            <PlanCardSubHeader className="bold">
              {course.certificate.expiration ? (
                t('TrainingPlans.certificationExpires', {
                  expirationDate: formatDate(course.certificate.expiration),
                })
              ) : (
                <>
                  <IconAlertTriangleFilled className="text-feedback-warning-base! size-4!" />
                  <span className="ml-1">
                    {t('TrainingPlans.noExpirationDate')}
                  </span>
                </>
              )}
            </PlanCardSubHeader>
          </>
        )}
        {isFailed && (
          <PlanCardSubHeader className="bold" data-testid="PlanTaskCount">
            {generateProgressText({
              finalScore: finalScore as string,
              isCompliance,
              isComplete,
              isFailed,
              isFoodSafety,
              timeSpentOnPlan: timeSpentOnPlan,
            })}
          </PlanCardSubHeader>
        )}
        {!isComplete && !isFailed && (
          <PlanCardSubHeader data-testid="PlanTaskCount">
            {getProgressBarText({
              isCompliance,
              isComplete,
              percentComplete: Number(percentComplete),
              timeSpentOnPlan: timeSpentOnPlan,
            })}
          </PlanCardSubHeader>
        )}
      </PlanCardContent>
      {!isComplete && !!course.dueDate && (
        <PlanCardTag
          color={getDueDateColor(course.dueDate)}
          text={getDueDateString(course.dueDate)}
          tooltip={t('TrainingPlans.accountability.due.dueDate', {
            dueDate: formatDate(course.dueDate),
          })}
        />
      )}
      {isComplete && (
        <PlanCardIcon
          icon={<Ribbon className="size-7!" />}
          tooltip={
            <>
              <strong>{t('TrainingPlans.statusOptions.completed')}:</strong>
              {formatDate(course.completedDate)}
            </>
          }
        />
      )}
    </PlanCard>
  );
};

export default MyPlansCompliancePlanCard;
